import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth.store';

/**
 * Query key prefixes that are user-scoped (NOT workspace-scoped).
 * These must NOT be cleared on workspace switch.
 */
const USER_SCOPED_PREFIXES = ['auth', 'current-user', 'workspaces'];

/**
 * Hook that encapsulates workspace switching with cache invalidation.
 *
 * On switch:
 * 1. Updates currentWorkspaceId in the auth store
 * 2. Removes all workspace-scoped query caches so components refetch
 *    fresh data for the new workspace
 *
 * User-scoped caches (auth, current-user, workspaces) are preserved.
 */
export function useWorkspaceSwitch() {
  const queryClient = useQueryClient();
  const setCurrentWorkspaceId = useAuthStore((s) => s.setCurrentWorkspaceId);

  const switchWorkspace = useCallback(
    (workspaceId: number) => {
      const previousId = useAuthStore.getState().currentWorkspaceId;
      if (workspaceId === previousId) return;

      const isWorkspaceScoped = (query: { queryKey: readonly unknown[] }) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key.length === 0) return false;
        const prefix = String(key[0]);
        return !USER_SCOPED_PREFIXES.includes(prefix);
      };

      // Cancel in-flight queries BEFORE updating workspace ID to prevent
      // stale responses from repopulating caches under the new workspace
      queryClient.cancelQueries({
        predicate: isWorkspaceScoped,
      });

      // Update workspace ID so new fetches use the correct header
      setCurrentWorkspaceId(workspaceId);

      // Remove all workspace-scoped query caches
      queryClient.removeQueries({
        predicate: isWorkspaceScoped,
      });
    },
    [queryClient, setCurrentWorkspaceId]
  );

  return { switchWorkspace };
}
